import { useEffect, useState } from "react";
import { IoMdArrowDropright } from "react-icons/io";
import { IoMdArrowDropleft } from "react-icons/io";
import { RootState, useAppDispatch, useAppSelector } from "../../store/store";
import { setActive } from "../../store/reducers/tileReducer";
import useWindowResize from "../../hooks/useWindowResize";
import useHasPermission from "../../hooks/useHasPermission";
import { contactGroupPermission } from "../../services/contactGroupPermission";
import { tracker } from "../../services/mixpanel";
import { useGetEquityContributionDataMutation } from "../../services/api";

type TileMapping = {
  [key: string]: string;
};

const permissionToTileMap: { [key: string]: string } = {
  learnMore: "Overview",
  additionalResources: "Additional Resources",
  scheduleCallWithCpa: "Schedule call with CPA",
  proforma: "Pro formas",
  equityContribution: "Equity Contribution",
  myDocuments: "My Documents",
  inviteOthers: "Invite Others",
  // signDocuments: "Sign Documents",
  contactUs: "Contact Us",
};

const tileMapping: TileMapping = {
  Overview: "tile1",
  "Additional Resources": "tile8",
  "Schedule call with CPA": "tile2",
  "Pro formas": "tile3",
  "Invite Others": "tile4",
  "Equity Contribution": "tile5",
  // "Sign Documents": "tile6",
  "My Documents": "tile7",
  "Contact Us": "contactUs",
};

interface IProps {
  apaData?: ApiResponse<IApaDoc>;
}

const SliderOption = ({ apaData }: IProps) => {
  const {
    userFlow: { rolePermissions },
  } = useAppSelector((state) => ({
    userFlow: state.userFlow,
  }));
  const { hasPermission } = useHasPermission();
  const stageData = useAppSelector((state: RootState) => state.stage);
  const permission = useAppSelector((state) => state.permission);
  const loginData = useAppSelector((state: RootState) => state.login);
  const dispatch = useAppDispatch();
  const [startIndex, setStartIndex] = useState(0);
  const { width: screenWidth } = useWindowResize();
  const [
      getEquityContributionData
  ] = useGetEquityContributionDataMutation();
  
  const zohoCustomerId = useAppSelector(
      (state: RootState) => state.login.zohoCustomerId
  );
  const [currentYearBool, setCurrentYearBool] = useState<boolean>(false);

  const fetchEquityData = async () => {
    try {
      const result = await getEquityContributionData(zohoCustomerId).unwrap();

      const details = result.data?.data.details.output; // string from API
      if (details && details !== "{}") {
        const parsed = details;
        const currentYear = new Date().getFullYear();

        if (
          parsed?.OppYear === currentYear &&
          parsed?.["Equity Contribution"] !== null
        ) {
          setCurrentYearBool(true);
        }
      }
    } catch (err) {
      setCurrentYearBool(false);
    }
  };

  useEffect(() => {
    fetchEquityData();
  }, []);

  const isDocumentSigned =
    !apaData?.data?.request_id ||
    (apaData?.data?.CAF_Signed &&
      apaData?.data?.MNDA_Signed &&
      apaData?.data?.OMA_Signed);

  const isVisible = (title: string) => {
    switch (title) {
      case "Equity Contribution":
        return  isDocumentSigned || !currentYearBool;
      case "Sign Documents":
        return apaData?.data?.request_id;
      case "My Documents":
        // First check if the user has the role permission
        // if (hasRole(loginData.role, "read:my-document")) {
        if (hasPermission("myDocuments")) {
          // For users with explicit role permission, we also check the existing conditions
          return (
            // stageData.softCommitComplete ||
            // hasPermission(permission, "only-my-document")
            stageData.softCommitComplete ||
            contactGroupPermission(permission, "showEmptyMyDocument")
          );
        }
        // For other users, use the original logic
        return (
          // stageData.softCommitComplete ||
          // hasPermission(permission, "only-my-document")
          stageData.softCommitComplete || hasPermission("myDocuments")
        );
      case "Pro formas":
        // Check if benefitCalculator is true
        return hasPermission("benefitCalculator");
      default:
        return true;
    }
  };

  const filterDataBasedOnPermission = () => {
    //   // Filter the data based on role permissions
    const rolePermissions = Object.keys(permissionToTileMap)
      // .filter((perm) => hasRole(role, perm as any))
      .filter(
        (perm) =>
          hasPermission(perm as keyof ITilePermissions) &&
          isVisible(permissionToTileMap[perm])
      )
      .map((perm) => permissionToTileMap[perm]);

    return rolePermissions;
  };
  // Filter data based on role permissions
  const tileNames = filterDataBasedOnPermission();

  const getVisibleItems = () => {
    const visibleItems: string[] = [];
    for (let i = 0; i < tileNames.length; i++) {
      const index = (startIndex + i) % tileNames.length;
      const item = tileNames[index];
      if (
        visibleItems.includes(item) ||
        visibleItems.length >= Math.min(screenWidth / 400, 5)
      )
        break;
      visibleItems.push(tileNames[index]);
    }

    return visibleItems;
  };

  const visibleTiles = getVisibleItems();

  const handleNext = () => {
    setStartIndex((prevIndex) => (prevIndex + 1) % visibleTiles.length);
  };

  const handlePrev = () => {
    setStartIndex((prevIndex) =>
      prevIndex === 0 ? tileNames.length - 1 : prevIndex - 1
    );
  };

  const handleNav = (item: string) => {
    setStartIndex(tileNames.indexOf(item));
    dispatch(setActive({ component: tileMapping[item] }));
    tracker.dashboard.navbar.onClick(item);
  };

  return (
    <div className="h-[70px] bg-[#FEFCFB] flex justify-evenly">
      <div
        onClick={handlePrev}
        className="h-full text-2xl text-[#000] flex items-center justify-center "
      >
        <IoMdArrowDropleft />
      </div>
      {!visibleTiles.length ? (
        <div className="flex justify-center items-center h-full w-full border-l-2 border-[#CAD0DC]">
          {rolePermissions?.length
            ? "no available tile for you"
            : "This user doesn't have any permission yet."}
        </div>
      ) : (
        visibleTiles?.map((item: string, index: number) => {
          return (
            <div
              key={index}
              onClick={() => handleNav(item)}
              className={`hover:bg-[#CAD0DC] text-[13px] font-semibold text-nowrap flex justify-center items-center h-full w-full border-l-2 border-[#CAD0DC] ${
                !index && "border-b-2 border-b-green-500"
              }`}
            >
              {item.toUpperCase()}
            </div>
          );
        })
      )}
      <div
        onClick={handleNext}
        className="h-full text-2xl text-[#000] border-l-2 border-[#CAD0DC] flex items-center justify-center"
      >
        <IoMdArrowDropright />
      </div>
    </div>
  );
};

export default SliderOption;
